import {
  AppWindow,
  Box,
  Boxes,
  Image as ImageIcon,
  Layers as LayersIcon,
  MessageSquareWarning,
  Milestone,
  PlaySquare,
  Shapes,
  Sigma,
  Smile,
  Tag,
  Type,
  Video,
  Workflow,
  type LucideIcon,
} from 'lucide-react';

import type { ElementType, Slide } from '@/lib/schema.types';
import { cn } from '@/lib/utils';
import { useEditorStore } from '@/store/editor.store';

const ICON: Record<ElementType, LucideIcon> = {
  text: Type,
  image: ImageIcon,
  video: Video,
  mcq: PlaySquare,
  chip: Tag,
  shape: Shapes,
  embed: AppWindow,
  '3d': Boxes,
  latex: Sigma,
  icon: Smile,
  callout: MessageSquareWarning,
  mermaid: Workflow,
  timeline: Milestone,
};

const LABEL: Record<ElementType, string> = {
  text: 'Text',
  image: 'Image',
  video: 'Video',
  mcq: 'Multiple Choice',
  chip: 'Chip',
  shape: 'Shape',
  embed: 'Embed',
  '3d': '3D Model',
  latex: 'Math (LaTeX)',
  icon: 'Icon',
  callout: 'Callout',
  mermaid: 'Mermaid Diagram',
  timeline: 'Timeline',
};

export function Layers({ slide }: { slide: Slide }) {
  const selection = useEditorStore((s) => s.selection);
  const setSelection = useEditorStore((s) => s.setSelection);

  return (
    <div className="border-t border-border-subtle">
      <div className="flex items-center justify-between px-4 py-2.5">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-fg-subtle">
          <LayersIcon size={12} /> Layers
        </div>
        <span className="text-[11px] text-fg-subtle">{slide.label}</span>
      </div>
      <ul className="max-h-64 overflow-y-auto pb-2 scroll-thin">
        {slide.rows.flatMap((row) =>
          row.columns.flatMap((col) =>
            col.elements.map((el) => {
              const Icon = ICON[el.type] ?? Box;
              const isActive =
                selection?.kind === 'element' && selection.elementId === el.id;
              const preview =
                el.type === 'text' && typeof el.content === 'string'
                  ? stripHtml(el.content).slice(0, 40) || LABEL[el.type]
                  : LABEL[el.type];
              return (
                <li key={el.id}>
                  <button
                    onClick={() =>
                      setSelection({
                        kind: 'element',
                        slideId: slide.id,
                        rowId: row.id,
                        columnId: col.id,
                        elementId: el.id,
                      })
                    }
                    className={cn(
                      'flex w-full items-center gap-2 px-4 py-1.5 text-left text-[13px] transition',
                      isActive
                        ? 'bg-accent-soft text-accent border-l-2 border-accent -ml-px'
                        : 'text-fg hover:bg-bg-subtle border-l-2 border-transparent -ml-px',
                    )}
                  >
                    <Icon size={13} className={isActive ? 'text-accent' : 'text-fg-muted'} />
                    <span className="truncate">{preview}</span>
                  </button>
                </li>
              );
            }),
          ),
        )}
        <li>
          <div className="flex w-full items-center gap-2 px-4 py-1.5 text-[13px] text-fg-muted">
            <Box size={13} />
            <span>Slide Container</span>
          </div>
        </li>
      </ul>
    </div>
  );
}

function stripHtml(s: string) {
  return s.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}
